import * as Flash from 'components/flash'
import { RasaContext } from 'context'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import React from 'react'
import { Roles } from 'shared/constants'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { Loading } from '../loading'
import { ScoutIcon } from '../icons/scout'
import {
  EmailAttributes, getAttribute,
} from '../email-layout/constants'
import { DashboardMenuOption } from '../dashboard-menu/constants'
import { EmailTemplates, StripoDefaultTemplates } from '../../shared_server_client/constants'
import { NewspaperTemplate } from '../icons/template-newspaper'
import { ClassicTemplate } from '../icons/template-classic'
import { BigAndBoldTemplate } from '../icons/template-big-and-bold'
import { InsightsTemplate } from '../icons/template-insights'
import { BlankTemplate } from '../icons/template-blank'
import {Dataset, DatasetParam} from '../../generic/dataset'
import { isNil } from 'lodash'
import { BaseClientEntity, RecordIdType } from '../../generic/baseClientEntity'
import { HeaderComponent } from '../header/component'
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap'
import { ProgressBar } from '@progress/kendo-react-progressbars'
import { UpgradeButton } from '../upgrade/button'
import { UPGRADE_BUTTON_COLOR } from '../upgrade/constants'
import './_styles.scss'
import * as GenericRedux from '../../generic/genericRedux'
import * as GA from '../../google-analytics'
import * as Constants from './constants'
import * as BillingPlan from 'shared_server_client/types/billing_plan'
import {AjaxWrapper, HttpMethod} from "../../generic/ajaxWrapper"
import * as GlobalConstants from '../../constants'
import {removeExtension} from "./utils"
import { TemplateChat } from './template-chat'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'

const NO_TEMPLATE_SELECTED = '-1'
const NO_TEMPLATE_SELECTED_LABEL = 'Select'
const ASK_SCOUT_TEMPLATE = 'ask-scout'

interface DesignDataProps {
  onChange: any,
  setConfigData?: any
}

type DesignProps = GenericRedux.AllComponentProps<DesignDataProps> & DesignDataProps

type emailTemplate = {
  id: string,
  body: string,
  fileName: string,
  type: 'stable' | 'custom',
  validation?: ValidationStatus
}

type ValidationStatus = {
  status: 'valid' | 'invalid' | 'pending' | 'not_validated',
  isValid: boolean,
  errorCount: number,
  warningCount: number,
  validatedAt?: string,
  sectionCounts?: any,
  errorCodes?: string[]
}

interface DesignState {
  communityId: string,
  communityPartnerData?: any,
  changes: any,
  deleteTemplateIndex: number,
  dirtySections?: any,
  emailLayoutConfig: any,
  emailTemplates: emailTemplate[],
  emailTemplatesData: any[],
  isCustomTemplateAllowed: boolean,
  isSuperUser: boolean,
  originalProps?: any,
  selectedTemplateForCreation?: string,
  showTemplateSelectionModal: boolean
  templateDeleteConfirmationModal: boolean,
  editTemplateModal: boolean,
  editingTemplate: emailTemplate | null,
  templateHtml: string,
  templatePreview: string,
  isLoadingTemplate: boolean,
  isSavingTemplate: boolean,
  isReloadingTemplates: boolean,
  saveAsMode: boolean,
  saveAsName: string,
  askScoutModal: boolean,
  scoutPrompt: string,
  isScoutLoading: boolean,
  documentation: any,
  selected: string,
  wasPreviouslyDirty: boolean,
  plan?: BillingPlan.BillingPlan,
  preSave: EmailTemplates,
  activeTab: string,
  showValidationModal: boolean,
  validationModalTemplate: emailTemplate | null,
  isValidating: boolean,
  validatingTemplate: string | null,
  validationResult: { success: boolean, message: string } | null
}

class TemplateLibraryComponent extends RasaReactComponent<DesignProps, DesignState> {
  public static contextType = RasaContext
  private sharedStore: SharedStore
  private emailLayoutId: number = null
  private templateChatRef: React.RefObject<TemplateChat>

  constructor(props: DesignProps) {
    super(props, 'email_layout', {
      communityId: '',
      emailLayoutConfig: {},
      emailTemplates: [],
      emailTemplatesData: [],
      deleteTemplateIndex: -1,
      isCustomTemplateAllowed: false,
      isSuperUser: false,
      changes: {},
      originalProps: {},
      dirtySections: {},
      showTemplateSelectionModal: false,
      templateDeleteConfirmationModal: false,
      editTemplateModal: false,
      editingTemplate: null,
      templateHtml: '',
      templatePreview: '',
      isLoadingTemplate: false,
      isSavingTemplate: false,
      saveAsMode: false,
      saveAsName: '',
      isReloadingTemplates: false,
      askScoutModal: false,
      scoutPrompt: '',
      isScoutLoading: false,
      documentation: {},
      selected: null,
      wasPreviouslyDirty: false,
      preSave: EmailTemplates.newspaper,
      activeTab: '1',
      showValidationModal: false,
      validationModalTemplate: null,
      isValidating: false,
      validatingTemplate: null,
      validationResult: null
    })
    this.templateChatRef = React.createRef()
  }

  public componentDidMount = () => {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.role),
    ])
      .then(([activeCommunity, role]) => {
        const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []
        if (activeCommunity.communityInfo) {
          this.emailLayoutId = activeCommunity.communityInfo.data.email_layouts.filter((s: any) => s.is_active)[0].id
          this.loadRecord(activeCommunity.communityId, this.emailLayoutId)
            .then(() => {
              this.setState({
                communityId: activeCommunity.communityId,
                emailLayoutConfig: this.props,
                isCustomTemplateAllowed: avlFeatures.indexOf(BillingPlanDetailCode.EMAIL_TEMPLATES) > -1,
                isSuperUser: role === Roles.super_admin,
                plan: activeCommunity.billingInfo.currentPlan,
                preSave: getAttribute(this.props, EmailAttributes.emailTemplate)
              }, () => {
                if (this.state.isCustomTemplateAllowed) {
                  this.loadEmailTemplates(activeCommunity.communityId)
                }
              })
            })
        }
      })
  }

  public mapPropsToEntityObject(entityObject: BaseClientEntity): void {
    Object.keys(this.state.changes).forEach((key: string) => {
      entityObject.data[key] = this.state.changes[key]
    })
  }

  public propertyChanged(field: string, value: string) {
    this.setState({
      isDirty: true,
      changes: {
        ...this.state.changes,
        [field]: value,
      },
    })
    this.props.propertyChanged(field, value)
  }

  public saveLayoutAndPublish = (recordId: RecordIdType) => {
    const selectedTemplate = getAttribute(this.props, EmailAttributes.emailTemplate)

    // Show validating indicator in the specific template card
    this.setState({
      isValidating: true,
      validatingTemplate: selectedTemplate,
      validationResult: null
    })

    // Check validation before publishing (but don't block on invalid status)
    this.checkTemplateValidation(selectedTemplate)
      .then((validationResult) => {
        // Don't block publishing - allow all templates to be published
        // Just show validation status for user awareness
        this.saveRecord(recordId).then(() => {
          const templateName = getAttribute(this.props, EmailAttributes.emailTemplate)
          this.setState({
            preSave: templateName,
            isValidating: false,
            validatingTemplate: null,
            validationResult: { success: true, message: 'Publishing...' }
          })
          this.clearChanges()
          // Need to reload page for the preview to reflect the new template.
          window.location.reload()
        }).catch((err) => {
          // Only show generic errors (not validation-related)
          // Validation errors should never block publishing
          let errorMessage = 'Failed to publish template'

          // Skip validation error display - only show non-validation errors
          if (err && err.error && err.error.validationError) {
            // Validation error - ignore and don't show to user
            this.setState({
              isValidating: false,
              validatingTemplate: null,
              validationResult: null,
              isSaving: false
            })
            return
          } else if (err && err.payload && err.payload.message) {
            errorMessage = err.payload.message
          } else if (err && err.message) {
            errorMessage = err.message
          }

          this.setState({
            isValidating: false,
            validatingTemplate: null,
            validationResult: { success: false, message: errorMessage },
            isSaving: false
          })

          // Clear error after 5 seconds
          setTimeout(() => {
            this.setState({ validationResult: null })
          }, 5000)
        })
      })
      .catch((err) => {
        // Validation check failed but don't show error to user
        // Proceed with publishing anyway
        this.saveRecord(recordId).then(() => {
          const templateName = getAttribute(this.props, EmailAttributes.emailTemplate)
          this.setState({
            preSave: templateName,
            isValidating: false,
            validatingTemplate: null,
            validationResult: { success: true, message: 'Publishing...' }
          })
          this.clearChanges()
          window.location.reload()
        }).catch((saveErr) => {
          // Only show non-validation errors
          this.setState({
            isValidating: false,
            validatingTemplate: null,
            validationResult: { success: false, message: 'Failed to publish template' },
            isSaving: false
          })

          setTimeout(() => {
            this.setState({ validationResult: null })
          }, 5000)
        })
      })
  }

  /**
   * Check template validation status
   * Returns validation result from backend
   */
  private checkTemplateValidation = async (templateValue: string): Promise<ValidationStatus> => {
    // Parse template identifier
    const templateParts = templateValue.split(':')
    const templateId = templateParts.length === 2 ? templateParts[1] : templateValue
    const templateType = templateParts.length === 2 ? templateParts[0] : null

    // Skip validation for default templates (not custom/stripo)
    if (!templateType || (templateType !== 'custom' && templateType !== 'stripo')) {
      return {
        status: 'valid',
        isValid: true,
        errorCount: 0,
        warningCount: 0
      }
    }

    try {
      // Pass the full template identifier (with prefix) to the validation endpoint
      // This allows the backend to determine the correct bucket and path
      const fullTemplateId = templateType ? `${templateType}:${templateId}` : templateId
      const url = `${AjaxWrapper.getServerUrl()}/stripo/templates/${this.state.communityId}/${encodeURIComponent(fullTemplateId)}/validation`
      const response = await AjaxWrapper.ajax(url, HttpMethod.GET, {})

      return response as ValidationStatus
    } catch (error) {
      // If validation check fails, assume valid to avoid blocking (fail open)
      return {
        status: 'not_validated',
        isValid: true,
        errorCount: 0,
        warningCount: 0
      }
    }
  }

  public render = () => {
    return (
      <div className="templates-design py-4 py-md-5">
        {this.state.isLoading || !this.state.plan || this.state.isSaving || this.state.isReloadingTemplates ? <Loading size="64"/> :
          <div className="container">
            <div className="template-editor">
              <div className="header-container">
                <HeaderComponent
                  title={''}
                  subTitle={'Template Library'}
                  description={['Select a template from below and begin designing.']}
                />
                {this.maxLimit().maxTemplateCount > 0 && <this.limitReached/>}
              </div>
              <div className="section">
                <div className="row">
                  <strong className="savedHeading">Standard Templates</strong>
                  <div className="col-12 col-sm-6 col-md-6 d-flex">
                    <button
                      className={`${this.buttonClass([EmailTemplates.default, EmailTemplates.oldDefault], 'classic')}`}
                      onClick={(e) => this.propertyChanged(EmailAttributes.emailTemplate, EmailTemplates.default)}>
                      <i className="fa fa-check-circle"
                         onClick={() => this.saveLayoutAndPublish(this.emailLayoutId)}></i>
                      <div className="d-flex align-items-start">
                        <ClassicTemplate colors={this.state.emailLayoutConfig}/>
                        <div className="templates-button-content">
                          <div className="templatetitle templates-title">
                            Classic
                          </div>
                          <span className="descriptionoftem">
                         A classic. Effective and simple. Includes small image articles.
                        </span>
                        </div>
                      </div>
                      <div className="templates-action-btns-wrap d-flex align-items-center justify-content-end">
                        <button type="button" className="btn btn-secondary ta-button"
                                onClick={(e) => { e.stopPropagation(); this.onCannedTemplateClick(EmailTemplates.default); }}>Edit
                        </button>
                      </div>
                    </button>
                  </div>
                  <div className="col-12 col-sm-6 col-md-6 d-flex">
                    <button
                      className={`${this.buttonClass([EmailTemplates.newspaper, EmailTemplates.oldNewspaper], 'news')}`}
                      onClick={(e) => this.propertyChanged(EmailAttributes.emailTemplate, EmailTemplates.newspaper)}>
                      <i className="fa fa-check-circle"
                         onClick={() => this.saveLayoutAndPublish(this.emailLayoutId)}></i>
                      <div className="d-flex align-items-start">
                        <NewspaperTemplate colors={this.state.emailLayoutConfig}/>
                        <div className="templates-button-content">
                          <div className="templatetitle templates-title">
                            Newspaper
                          </div>
                          <span className="descriptionoftem">
                            A newspaper style newsletter template. Includes a large first article.
                          </span>
                        </div>
                      </div>
                      <div className="templates-action-btns-wrap d-flex align-items-center justify-content-end">
                        <button type="button" className="btn btn-secondary ta-button"
                                onClick={(e) => { e.stopPropagation(); this.onCannedTemplateClick(EmailTemplates.newspaper); }}>Edit
                        </button>
                      </div>
                    </button>
                  </div>
                  <div className="col-12 col-sm-6 col-md-6 d-flex">
                    <button
                      className={`${this.buttonClass([EmailTemplates.bigAndBold, EmailTemplates.oldBigAndBold], 'bold')}`}
                      onClick={(e) => this.propertyChanged(EmailAttributes.emailTemplate, EmailTemplates.bigAndBold)}>
                      <i className="fa fa-check-circle"
                         onClick={() => this.saveLayoutAndPublish(this.emailLayoutId)}></i>
                      <div className="d-flex align-items-start">
                        <BigAndBoldTemplate colors={this.state.emailLayoutConfig}/>
                        <div className="templates-button-content">
                          <div className="templatetitle templates-title">
                            Big and Bold
                          </div>
                          <span className="descriptionoftem">Large size article images and bolder fonts.</span>
                        </div>
                      </div>
                      <div className="templates-action-btns-wrap d-flex align-items-center justify-content-end">
                        <button type="button" className="btn btn-secondary ta-button"
                                onClick={(e) => { e.stopPropagation(); this.onCannedTemplateClick(EmailTemplates.bigAndBold); }}>Edit
                        </button>
                      </div>
                    </button>
                  </div>
                  <div className="col-12 col-sm-6 col-md-6 d-flex">
                    <button
                      className={`${this.buttonClass([EmailTemplates.insights], 'insights')}`}
                      onClick={(e) => this.propertyChanged(EmailAttributes.emailTemplate, EmailTemplates.insights)}>
                      <i className="fa fa-check-circle"
                         onClick={() => this.saveLayoutAndPublish(this.emailLayoutId)}></i>
                      <div className="d-flex align-items-start">
                        <InsightsTemplate colors={this.state.emailLayoutConfig}/>
                        <div className="templates-button-content">
                          <div className="templatetitle templates-title">
                            Insights
                          </div>
                          <span className="descriptionoftem">Focused on text and information.</span>
                        </div>
                      </div>
                      <div className="templates-action-btns-wrap d-flex align-items-center justify-content-end">
                        <button type="button" className="btn btn-secondary ta-button"
                                onClick={(e) => { e.stopPropagation(); this.onCannedTemplateClick(EmailTemplates.insights); }}>
                          Edit
                        </button>
                      </div>
                    </button>
                  </div>
                  {this.state.emailTemplates.length > 0 && this.state.isCustomTemplateAllowed &&
                    <>
                      <strong className="savedHeading">Saved Templates</strong>
                      {this.state.emailTemplates.map((template, index) => {
                        const validation = template.validation
                        const isInvalid = validation && validation.status === 'invalid'
                        const isPending = validation && validation.status === 'pending'
                        const hasWarnings = validation && validation.warningCount > 0

                        return (
                          <div className="col-12 col-sm-6 col-md-4 d-flex" key={index}>
                            <button
                              onClick={(e) => this.propertyChanged(EmailAttributes.emailTemplate, this.tokenizeTemplateName(
                                template.type === Constants.TEMPLATE_VERSION.V1 ? template.id : template.fileName, template.type))}
                              className={`${this.buttonClass([template.type === Constants.TEMPLATE_VERSION.V1 ? template.id : template.fileName], 'custom')} ${(this.state.isSuperUser && isInvalid) ? 'invalid' : ''}`}>

                              {/* Validation Badge - Clickable (Only for Super Users) */}
                              {this.state.isSuperUser && isInvalid && (
                                <div
                                  className="validation-badge error clickable"
                                  title="Click for details"
                                  onClick={(e) => { e.stopPropagation(); this.showValidationDetails(template); }}>
                                  <i className="fas fa-exclamation-circle"></i>
                                  Invalid
                                </div>
                              )}
                              {this.state.isSuperUser && isPending && (
                                <div
                                  className="validation-badge pending clickable"
                                  title="Click for details"
                                  onClick={(e) => { e.stopPropagation(); this.showValidationDetails(template); }}>
                                  <i className="fas fa-clock"></i>
                                  Validating...
                                </div>
                              )}
                              {this.state.isSuperUser && !isInvalid && !isPending && hasWarnings && (
                                <div
                                  className="validation-badge warning clickable"
                                  title="Click for details"
                                  onClick={(e) => { e.stopPropagation(); this.showValidationDetails(template); }}>
                                  <i className="fas fa-exclamation-triangle"></i>
                                  {validation.warningCount} Warning{validation.warningCount !== 1 ? 's' : ''}
                                </div>
                              )}

                              {/* Check circle / Validation progress */}
                              {(() => {
                                const templateIdentifier = this.tokenizeTemplateName(
                                  template.type === Constants.TEMPLATE_VERSION.V1 ? template.id : template.fileName,
                                  template.type
                                )
                                const isValidatingThis = this.state.validatingTemplate === templateIdentifier
                                const hasResult = this.state.validationResult && this.state.validatingTemplate === templateIdentifier

                                // Check if this template is selected
                                const selectedTemplate = this.unTokenizeTemplateName(getAttribute(this.props, EmailAttributes.emailTemplate))
                                const templateName = template.type === Constants.TEMPLATE_VERSION.V1 ? template.id : template.fileName
                                const isSelected = selectedTemplate === templateName

                                if (isValidatingThis) {
                                  return (
                                    <div className="validation-progress-overlay">
                                      <div className="validation-spinner">
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <span>Validating...</span>
                                      </div>
                                    </div>
                                  )
                                } else if (hasResult) {
                                  return (
                                    <div className={`validation-result-overlay ${this.state.validationResult.success ? 'success' : 'error'}`}>
                                      <div className="validation-result-content">
                                        <i className={`fas ${this.state.validationResult.success ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                        <span>{this.state.validationResult.message}</span>
                                      </div>
                                    </div>
                                  )
                                }

                                // Only show publish button if template is selected
                                if (!isSelected) {
                                  return null
                                }

                                // Show publish button for all templates (including invalid ones)
                                // Users can publish even if validation fails
                                // Only show validation warning in tooltip for super users
                                const tooltipText = (this.state.isSuperUser && isInvalid)
                                  ? "Publish (template has validation warnings)"
                                  : "Publish template"

                                return (
                                  <i className="fa fa-check-circle"
                                     title={tooltipText}
                                     onClick={() => this.saveLayoutAndPublish(this.emailLayoutId)}></i>
                                )
                              })()}

                              <div className="d-flex align-items-start">
                                <ClassicTemplate colors={this.state.emailLayoutConfig}/>
                                <div className="templates-button-content">
                                  <div onClick={() => this.props.push(DashboardMenuOption.templateLibrary)}
                                       className="templatetitle templates-title">
                                    {template.fileName}
                                  </div>
                                  <div
                                    className={'descriptionoftem'}>
                                    {template.type === Constants.TEMPLATE_VERSION.V2 ? 'Custom rasa.io Template' : 'Imported HTML'}
                                  </div>
                                </div>
                              </div>
                              <div className="templates-action-btns-wrap d-flex align-items-center justify-content-end">
                                {/* Fix button removed from card - available in modal for space savings */}
                                {template.type === Constants.TEMPLATE_VERSION.V2 && (
                                  <>
                                    {!this.maxLimit().maxLimitReached &&
                                      <button type="button" className="btn btn-secondary ta-button"
                                              onClick={(e) => { e.stopPropagation(); this.copyTemplate(template); }}>
                                        Copy
                                      </button>
                                    }
                                    <button type="button" className="btn btn-secondary ta-button"
                                            onClick={(e) => { e.stopPropagation(); this.onClick(template.fileName); }}>
                                      Edit
                                    </button>
                                  </>
                                )}
                                {template.type === Constants.TEMPLATE_VERSION.V1 && (
                                  <button type="button" className="btn btn-secondary ta-button"
                                          onClick={(e) => { e.stopPropagation(); this.editTemplate(template); }}>
                                    Edit
                                  </button>
                                )}
                                {this.state.preSave !== this.tokenizeTemplateName(
                                    template.type === Constants.TEMPLATE_VERSION.V1 ? template.id : template.fileName,
                                    template.type
                                  ) && (
                                  <button type="button" className="btn btn-secondary ta-button"
                                          onClick={(e) => { e.stopPropagation(); this.deleteTemplateConfirmation(index); }}>
                                    Delete
                                  </button>
                                )}
                              </div>
                            </button>
                          </div>
                        )
                      })
                      }
                    </>
                  }
                </div>
              </div>
            </div>
            {this.renderDeleteConfirmationModal()}
            {this.getTemplateSelectionModal()}
            {this.renderEditTemplateModal()}
            {this.renderValidationModal()}
          </div>
        }
      </div>)
  }
  protected limitReached = () => {
    const {maxTemplateCount, isUnlimited, maxLimitReached} = this.maxLimit()
    return <div className="design-right-side">
      <div className="template-button-container">
        {!isUnlimited && this.state.isCustomTemplateAllowed && maxTemplateCount > 0 ?
          <div className="div-progress-bar">
            <ProgressBar
              min={0}
              className={`progress-bar${maxLimitReached ? '-red' : ''}`}
              max={maxTemplateCount}
              value={this.state.emailTemplates.length}
              labelVisible={false}/>
            <div className="progress-bar-label">
              <div>Templates used</div>
              <div>{this.state.emailTemplates.length}/{maxTemplateCount}</div>
            </div>
          </div> : null}
        {maxLimitReached || !this.state.isCustomTemplateAllowed ?
          <div>
            <UpgradeButton color={UPGRADE_BUTTON_COLOR.WHITE} showIcon={true}
                           source={GA.UpgradeSource.CustomTemplate} text="Upgrade"/>
          </div> :
          <div className="add-sources-button-text-section content-right">
            <Button className="w-100" onClick={() => this.setState({showTemplateSelectionModal: true})}>
              <span className="button-text">
                Create Template
              </span>
            </Button>
            <a className="link" href={GlobalConstants.RASA_HELP_CUSTOM_TEMPLATE_EDITOR} target="_blank" rel="noopener">
            <div className="verbiage">
              <div>How to utilize the custom {GlobalConstants.BRAND_NAME} template builder</div>
            </div>
            </a>
          </div>}
      </div>
    </div>
  }

  private maxLimit = () => {
    const maxTemplateCount = BillingPlan.getCurrentPlanMaxTemplate(this.state.plan)
    const isUnlimited = maxTemplateCount === BillingPlan.UNLIMITED_VALUE || maxTemplateCount === -1
    const maxLimitReached = !isUnlimited &&
      this.state.emailTemplates.length >= maxTemplateCount
    return {
      maxTemplateCount,
      isUnlimited,
      maxLimitReached
    }
  }

  private loadEmailTemplates = (communityId: string) => {
    const params: DatasetParam[] = [
      { param: 'load_stripo', value: '1' },
    ]
    return new Dataset().loadCommunityDataset('htmlTemplates', communityId, params)
      .then((response) => {
        const htmlTemplates = response[0]
        const data = [
          {
            key: NO_TEMPLATE_SELECTED_LABEL,
            value: NO_TEMPLATE_SELECTED,
          },
          ...htmlTemplates.map((x) => {
            return {
              key: x.fileName || x.id,
              value: x.id,
            }
          })
        ]
        this.setState({
          emailTemplatesData: data,
          emailTemplates: htmlTemplates.map((x) => ({
            ...x,
            id: x.type !== Constants.TEMPLATE_VERSION.V1 ? removeExtension(x.id) : x.id,
            fileName: removeExtension(x.fileName || x.id),
          })),
          selected: this.getSelectedTemplate(),
        })
      })
  }

  private onCannedTemplateClick = (template: string) => {
    this.props.push(`${DashboardMenuOption.emailDesign}?template=${template}`)
  }

  private onClick = (template: string) => {
    this.props.push(`${DashboardMenuOption.customDesign}?template=${this.unTokenizeTemplateName(template)}`)
  }

  private editTemplate = (template: emailTemplate) => {
    this.setState({
      editTemplateModal: true,
      editingTemplate: template,
      isLoadingTemplate: true,
      templateHtml: '',
      templatePreview: ''
    })
    this.loadTemplateForEdit(template)
    this.fetchTemplateDocumentation()
  }

  /**
   * Show validation details modal
   * Displays comprehensive validation information
   */
  private showValidationDetails = (template: emailTemplate) => {
    this.setState({
      showValidationModal: true,
      validationModalTemplate: template
    })
  }

  /**
   * Close validation details modal
   */
  private closeValidationModal = () => {
    this.setState({
      showValidationModal: false,
      validationModalTemplate: null
    })
  }

  /**
   * Fix template with Scout AI
   * Opens edit modal with Scout tab active and pre-populates error message
   */
  private fixWithScout = (template: emailTemplate) => {
    const validation = template.validation

    if (!validation || validation.status !== 'invalid') {
      return
    }

    // Build detailed error message with explanations
    let errorMessage = 'This template has CANSPAM compliance issues that need to be fixed:\n\n'

    if (validation.errorCodes && validation.errorCodes.length > 0) {
      // Get error messages mapping
      const errorMessages: any = {
        'EMPTY_TEMPLATE': 'Template is empty or too short',
        'NO_FOOTER': 'Missing footer section with data-rasa-module="footer"',
        'NO_CONTENT_SECTIONS': 'Template has no article or content sections',
        'MISSING_UNSUBSCRIBE': 'Footer missing unsubscribe link (data-rasa-attribute="unsubscribe_url")',
        'MISSING_COMPANY_NAME': 'Footer missing company name (data-rasa-attribute="company_name")',
        'MISSING_COMPANY_ADDRESS': 'Footer missing physical address (data-rasa-attribute="company_address")',
        'AI_VALIDATION_FAILED': 'AI could not verify CANSPAM compliance - footer may be missing required elements',
        'INVALID_HTML_STRUCTURE': 'Template has invalid HTML structure'
      }

      // Add each error with its explanation
      validation.errorCodes.forEach((code: string) => {
        const explanation = errorMessages[code] || code
        errorMessage += `• ${explanation}\n`
      })

      errorMessage += '\n'
    }

    errorMessage += 'Please fix these issues to make the template CANSPAM compliant. The footer should include:\n'
    errorMessage += '1. An unsubscribe link with data-rasa-attribute="unsubscribe_url"\n'
    errorMessage += '2. Company name with data-rasa-attribute="company_name"\n'
    errorMessage += '3. Physical mailing address with data-rasa-attribute="company_address"\n\n'
    errorMessage += 'Please update the template HTML to add these required elements.'

    // Open edit modal in Scout tab with error message ready
    this.setState({
      editTemplateModal: true,
      editingTemplate: template,
      isLoadingTemplate: true,
      templateHtml: '',
      templatePreview: '',
      activeTab: '2', // Scout tab
      scoutPrompt: errorMessage
    })

    // Load template (will auto-send message after loading)
    this.loadTemplateForEdit(template)
    this.fetchTemplateDocumentation()
  }

  private loadTemplateForEdit = (template: emailTemplate) => {
    const url = `${AjaxWrapper.getServerUrl()}/entity/${this.state.communityId}/htmlTemplates/${template.id}`
    AjaxWrapper.ajax(url, HttpMethod.GET, {})
      .then((response) => {
        const htmlContent = response.body || ''
        this.setState({
          templateHtml: htmlContent,
          templatePreview: htmlContent,
          isLoadingTemplate: false
        }, () => {
          // After template loads, if scoutPrompt is set, auto-send it
          if (this.state.scoutPrompt && this.state.scoutPrompt.trim() !== '' && this.templateChatRef.current) {
            setTimeout(() => {
              if (this.templateChatRef.current) {
                this.templateChatRef.current.sendMessage(this.state.scoutPrompt)
              }
            }, 300)
          }
        })
      })
      .catch((error) => {
        this.setState({
          templateHtml: '',
          templatePreview: '',
          isLoadingTemplate: false
        })
      })
  }

  private updateTemplateHtml = (html: string) => {
    this.setState({
      templateHtml: html,
      templatePreview: html
    })
  }

  private saveTemplate = () => {
    if (!this.state.editingTemplate) {
      return
    }

    if (!this.state.templateHtml || !this.state.templateHtml.trim()) {
      this.context.store.dispatch(Flash.showFlashError('Cannot save empty template. Please add content first.'))
      return
    }

    this.setState({
      isSavingTemplate: true
    })

    // Create file object for the update method
    const htmlFile = new File([this.state.templateHtml], this.state.editingTemplate.fileName, {
      type: 'text/html'
    })

    // Use context to get entity object and call update
    this.context.entityMetadata.getEntityObject('htmlTemplates')
      .then((entityObject: BaseClientEntity) => {
        entityObject.setRecordId(this.state.communityId, this.state.editingTemplate.id)

        // Upload with the HTML file - this will trigger the update method in htmlTemplateServerEntity
        // Note: Validation is now lenient on the backend and won't block saves
        return entityObject.upload('html', htmlFile, {})
      })
      .then(() => {
        const savedTemplate = this.state.editingTemplate

        this.setState({
          isSavingTemplate: false,
          editTemplateModal: false,
          editingTemplate: null,
          templateHtml: '',
          templatePreview: ''
        })

        // Reload templates to get validation result
        this.loadEmailTemplates(this.state.communityId).then(() => {
          const templateIdentifier = this.tokenizeTemplateName(
            savedTemplate.type === Constants.TEMPLATE_VERSION.V1 ? savedTemplate.id : savedTemplate.fileName,
            savedTemplate.type
          )

          // Run validation check (but don't show messages about results)
          this.checkTemplateValidation(templateIdentifier)
            .then((validationResult) => {
              // Validation completed - badges will update automatically
              this.context.store.dispatch(Flash.showFlashMessage('Template saved successfully'))
            })
            .catch((err) => {
              // Validation check failed - still show success message
              this.context.store.dispatch(Flash.showFlashMessage('Template saved successfully'))
            })
        })
      })
      .catch((error) => {
        this.setState({
          isSavingTemplate: false
        })

        // Show user-friendly error message
        const errorMessage = (error && error.payload && error.payload.message) || (error && error.message) || 'Failed to save template. Please try again.'
        this.context.store.dispatch(Flash.showFlashError(errorMessage))
      })
  }

  private closeEditTemplateModal = () => {
    this.setState({
      editTemplateModal: false,
      editingTemplate: null,
      templateHtml: '',
      templatePreview: '',
      isLoadingTemplate: false,
      isSavingTemplate: false,
      isReloadingTemplates: false,
      saveAsMode: false,
      saveAsName: '',
      askScoutModal: false,
      scoutPrompt: '',
      isScoutLoading: false,
      activeTab: '1'
    })
  }

  private fetchTemplateDocumentation = () => {
    const url: string = `${AjaxWrapper.getServerUrl()}/email/documentation`
    return AjaxWrapper.ajax(url, HttpMethod.GET, {})
      .then((response) => {
        this.setState({
          documentation: response,
        })
      })
      .catch((error) => {
        // 'Failed to fetch documentation'
      })
  }

  private toggleSaveAsMode = () => {
    this.setState(prevState => ({
      saveAsMode: !prevState.saveAsMode,
      saveAsName: !prevState.saveAsMode ? `${prevState.editingTemplate && prevState.editingTemplate.fileName || 'template'}_copy` : ''
    }))
  }

  private handleSaveAsNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      saveAsName: e.target.value
    })
  }


  private saveAsNewTemplate = () => {
    if (!this.state.saveAsName.trim()) {
      this.context.store.dispatch(Flash.showFlashError('Please enter a name for the new template'))
      return
    }

    if (!this.state.templateHtml || !this.state.templateHtml.trim()) {
      this.context.store.dispatch(Flash.showFlashError('Cannot save empty template. Please add content first.'))
      return
    }

    this.setState({
      isSavingTemplate: true
    })

    // Create a Blob file from the HTML content
    const htmlBlob = new Blob([this.state.templateHtml], { type: 'text/html' })
    const fileName = this.state.saveAsName.endsWith('.html') ? this.state.saveAsName : `${this.state.saveAsName}.html`
    const file = new File([htmlBlob], fileName, { type: 'text/html' })

    this.context.entityMetadata.getEntityObject('htmlTemplates')
      .then((entityObject) => {
        entityObject.setRecordId(this.state.communityId, null) // null for new template
        const uploadOptions = {
          forceUpload: false  // Note: Validation is lenient on backend and won't block saves
        }
        return entityObject.upload('html', file, uploadOptions)
          .then(() => {
            this.setState({
              editTemplateModal: false,
              editingTemplate: null,
              templateHtml: '',
              templatePreview: '',
              isLoadingTemplate: false,
              isSavingTemplate: false,
              isReloadingTemplates: true,
              saveAsMode: false,
              saveAsName: '',
              askScoutModal: false,
              scoutPrompt: '',
              isScoutLoading: false
            })
            // Reload templates to show the new one
            this.loadEmailTemplates(this.state.communityId).then(() => {
              this.setState({
                isReloadingTemplates: false
              })

              // Check validation status (validation completed during save)
              const newTemplate = this.state.emailTemplates.find(t =>
                t.fileName === fileName.replace('.html', '')
              )

              if (newTemplate) {
                const templateIdentifier = this.tokenizeTemplateName(
                  newTemplate.type === Constants.TEMPLATE_VERSION.V1 ? newTemplate.id : newTemplate.fileName,
                  newTemplate.type
                )

                // Run validation check (but don't show messages about results)
                this.checkTemplateValidation(templateIdentifier)
                  .then((validationResult) => {
                    // Validation completed - badges will update automatically
                    this.context.store.dispatch(Flash.showFlashMessage(`Template saved as "${fileName}"!`))
                  })
                  .catch((err) => {
                    // Validation check failed - still show success message
                    this.context.store.dispatch(Flash.showFlashMessage(`Template saved as "${fileName}"!`))
                  })
              } else {
                this.context.store.dispatch(Flash.showFlashMessage(`Template saved as "${fileName}"!`))
              }
            }).catch(() => {
              this.setState({
                isReloadingTemplates: false
              })
            })
          })
          .catch((error) => {
            this.setState({
              isSavingTemplate: false
            })

            const errorMessage = (error && error.payload && error.payload.message) || (error && error.message) || 'Failed to save new template. Please try again.'
            this.context.store.dispatch(Flash.showFlashError(errorMessage))
          })
      })
      .catch((error) => {
        this.setState({
          isSavingTemplate: false
        })

        const errorMessage = (error && error.payload && error.payload.message) || (error && error.message) || 'Failed to save new template. Please try again.'
        this.context.store.dispatch(Flash.showFlashError(errorMessage))
      })
  }

  private getSelectedTemplate = () => {
    const currentTemplate = getAttribute(this.props, EmailAttributes.emailTemplate)
    return !isNil(currentTemplate) && currentTemplate.indexOf('custom') > -1 &&
    this.state.emailTemplatesData.length > 0 ? this.state.emailTemplatesData.filter((x) =>
        x.value === currentTemplate.split(':')[1])[0].key :
      this.state.emailTemplatesData.length > 0 ? this.state.emailTemplatesData[0].key : ''
  }

  private buttonClass = (templateNames: string[], prefix: string): string => {
    const selectedTemplate = this.unTokenizeTemplateName(getAttribute(this.props, EmailAttributes.emailTemplate))
    const isSelected: boolean = templateNames.includes(selectedTemplate)
      || (templateNames.includes('custom') && selectedTemplate && selectedTemplate.indexOf('custom') > -1)
    const isSaved: boolean = templateNames.includes(this.state.preSave)
    const isPublish: boolean = getAttribute(this.props, EmailAttributes.emailTemplate) === this.state.preSave
    return `template ${prefix}-temp ${isSelected ? 'selected' : ''} ${isSaved ? "saved" : ''} ${isPublish ? "publish" : ''}`
  }
  private clearChanges = () => {
    this.setState({
      changes: {}
    })
  }

  private copyTemplate = (template: emailTemplate) => {
    const url: string = `${AjaxWrapper.getServerUrl()}/${this.state.communityId}/template/copy`
    this.setState({
      isSaving: true,
    })
    AjaxWrapper.ajax(url, HttpMethod.POST, {
      templateId: template.id,
      path: `copy_${template.fileName}`,
    })
      .then(() => {
        this.loadEmailTemplates(this.state.communityId).then(() =>{
          this.setState({
            isSaving: false,
          })
        })
      })
      .catch(() => {
        this.setState({
          isSaving: false,
        })
      })
  }
  private deleteTemplate() {
    this.setState({
      isSaving: true,
    })
    const templateId = this.state.emailTemplates[this.state.deleteTemplateIndex].id
    const url: string = `${AjaxWrapper.getServerUrl()}/${this.state.communityId}/template/${templateId}`
    AjaxWrapper.ajax(url, HttpMethod.DELETE, null)
      .then(() => {
        this.state.emailTemplates.splice(this.state.deleteTemplateIndex, 1)
        this.setState({
          isSaving: false,
          templateDeleteConfirmationModal: false,
          deleteTemplateIndex: -1
        })
      })
      .catch(() => {
        this.setState({
          isSaving: false,
        })
      })
  }

  private deleteTemplateConfirmation(index: number) {
    this.setState({
      templateDeleteConfirmationModal: true,
      deleteTemplateIndex: index
    })
  }

  private closeDeleteConfirmationModal = () => {
    this.setState({
      templateDeleteConfirmationModal: false,
      deleteTemplateIndex: -1
    })
  }

  private closeTemlateSelectionModal = () => {
    this.setState({
      showTemplateSelectionModal: false,
      selectedTemplateForCreation: "",
    })
  }

  private renderDeleteConfirmationModal(): JSX.Element {
    return (
      <div>
        <Modal isOpen={this.state.templateDeleteConfirmationModal}>
          <ModalBody className="delete-modal">
            <div>
              <h4>Are You Sure?</h4>
              <p>Are you sure you want to delete this template?</p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => this.deleteTemplate()}>
              Yes
            </Button>
            <Button onClick={this.closeDeleteConfirmationModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }

  /**
   * Render Validation Details Modal
   * Comprehensive view of template validation status
   */
  private renderValidationModal(): JSX.Element {
    const template = this.state.validationModalTemplate
    const validation = template && template.validation

    if (!template || !validation) {
      return null
    }

    const isValid = validation.status === 'valid'
    const isInvalid = validation.status === 'invalid'
    const isPending = validation.status === 'pending'
    const notValidated = validation.status === 'not_validated'

    // Error code to user-friendly message mapping
    const errorMessages = {
      'EMPTY_TEMPLATE': {
        title: 'Empty Template',
        description: 'Template must contain at least 100 characters of content.',
        solution: 'Add content to your template including header, articles, and footer sections.'
      },
      'NO_FOOTER': {
        title: 'Missing Footer',
        description: 'Template must include a footer module for CANSPAM compliance.',
        solution: 'Add a footer with data-rasa-module="footer" attribute to your template.'
      },
      'MISSING_UNSUBSCRIBE': {
        title: 'Missing Unsubscribe Link',
        description: 'Footer must include an unsubscribe link (required by CANSPAM law).',
        solution: 'Add an element with data-rasa-attribute="unsubscribe_url" in your footer.'
      },
      'MISSING_COMPANY_NAME': {
        title: 'Missing Company Name',
        description: 'Footer must include your company name (required by CANSPAM law).',
        solution: 'Add an element with data-rasa-attribute="company_name" in your footer.'
      },
      'MISSING_COMPANY_ADDRESS': {
        title: 'Missing Company Address',
        description: 'Footer must include your physical mailing address (required by CANSPAM law).',
        solution: 'Add an element with data-rasa-attribute="company_address" in your footer.'
      },
      'NO_CONTENT_SECTIONS': {
        title: 'No Content Sections',
        description: 'Template does not contain any article or content sections.',
        solution: 'Add article containers with data-rasa-container="articles" to display newsletter content.'
      },
      'INVALID_HTML_STRUCTURE': {
        title: 'Invalid HTML Structure',
        description: 'Template HTML cannot be parsed or contains structural errors.',
        solution: 'Check your HTML for syntax errors and ensure it is well-formed.'
      },
      'CANSPAM_NON_COMPLIANT': {
        title: 'CANSPAM Non-Compliant',
        description: 'Template does not meet CANSPAM compliance requirements.',
        solution: 'Ensure your footer includes unsubscribe link, company name, and physical address.'
      },
      'AI_VALIDATION_FAILED': {
        title: 'CANSPAM Validation Failed',
        description: 'AI validation could not verify CANSPAM compliance. Your template footer may be missing required elements:',
        solution: 'Ensure your footer (data-rasa-module="footer") contains: 1) Unsubscribe link (data-rasa-attribute="unsubscribe_url"), 2) Company name (data-rasa-attribute="company_name"), 3) Physical address (data-rasa-attribute="company_address"). Click "Fix with Scout" below to auto-fix these issues.'
      },
      'AI_VALIDATION': {
        title: 'CANSPAM Validation Issue',
        description: 'Template failed CANSPAM compliance validation.',
        solution: 'Ensure your footer includes all required CANSPAM elements. Use "Fix with Scout" to automatically correct these issues.'
      }
    }

    return (
      <div className="validation-modal-wrapper">
        <Modal isOpen={this.state.showValidationModal} size="lg">
          <ModalHeader className={`validation-modal-header ${isValid ? 'valid' : isInvalid ? 'invalid' : 'pending'}`}>
            <div className="validation-modal-title">
              {isValid && <i className="fas fa-check-circle mr-2"></i>}
              {isInvalid && <i className="fas fa-exclamation-circle mr-2"></i>}
              {isPending && <i className="fas fa-clock mr-2"></i>}
              {notValidated && <i className="fas fa-question-circle mr-2"></i>}
              Template Validation
            </div>
            <div className="validation-modal-template-name">{template.fileName}</div>
          </ModalHeader>
          <ModalBody className="validation-modal-body">
            {/* Status Overview */}
            <div className="validation-status-section">
              <h5>Status</h5>
              <div className={`validation-status-badge ${validation.status}`}>
                {isValid && '✅ Valid - Ready to Publish'}
                {isInvalid && '❌ Invalid - Cannot Publish'}
                {isPending && '⏳ Validation in Progress'}
                {notValidated && '❓ Not Yet Validated'}
              </div>
              {validation.validatedAt && (
                <div className="validation-timestamp">
                  Last validated: {new Date(validation.validatedAt).toLocaleString()}
                </div>
              )}
            </div>

            {/* Errors Section */}
            {isInvalid && validation.errorCodes && validation.errorCodes.length > 0 && (
              <div className="validation-errors-section">
                <h5>
                  <i className="fas fa-exclamation-circle mr-2" style={{color: '#dc3545'}}></i>
                  Errors ({validation.errorCount})
                </h5>
                <p className="validation-section-description">
                  These issues must be fixed before you can publish this template.
                </p>
                <div className="validation-issues-list">
                  {validation.errorCodes.map((errorCode, index) => {
                    const errorInfo = errorMessages[errorCode] || {
                      title: errorCode,
                      description: 'Validation error detected.',
                      solution: 'Please review your template and fix this issue.'
                    }
                    return (
                      <div key={index} className="validation-issue-item error">
                        <div className="validation-issue-icon">
                          <i className="fas fa-times-circle"></i>
                        </div>
                        <div className="validation-issue-content">
                          <div className="validation-issue-title">{errorInfo.title}</div>
                          <div className="validation-issue-description">{errorInfo.description}</div>
                          <div className="validation-issue-solution">
                            <strong>Solution:</strong> {errorInfo.solution}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Warnings Section */}
            {validation.warningCount > 0 && (
              <div className="validation-warnings-section">
                <h5>
                  <i className="fas fa-exclamation-triangle mr-2" style={{color: '#ffc107'}}></i>
                  Warnings ({validation.warningCount})
                </h5>
                <p className="validation-section-description">
                  These are recommendations but won't prevent publishing.
                </p>
              </div>
            )}

            {/* Section Counts */}
            {validation.sectionCounts && Object.keys(validation.sectionCounts).length > 0 && (
              <div className="validation-sections-section">
                <h5>
                  <i className="fas fa-th-list mr-2"></i>
                  Template Structure
                </h5>
                <p className="validation-section-description">
                  Article slots and modules detected in this template.
                </p>
                <div className="validation-section-counts">
                  {Object.entries(validation.sectionCounts).map(([sectionType, count]: [string, any]) => (
                    <div key={sectionType} className="validation-section-count-item">
                      <span className="section-type">{sectionType}</span>
                      <span className="section-count">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="validation-section-note">
                  <i className="fas fa-info-circle mr-2"></i>
                  These counts determine how many articles can be included in newsletters using this template.
                </div>
              </div>
            )}

            {/* Success Message */}
            {isValid && (
              <div className="validation-success-section">
                <div className="validation-success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h5>Template is Valid!</h5>
                <p>
                  This template passed all validation checks and meets CANSPAM compliance requirements.
                  You can safely publish this template.
                </p>
              </div>
            )}

            {/* Pending Message */}
            {isPending && (
              <div className="validation-pending-section">
                <div className="validation-pending-icon">
                  <i className="fas fa-clock spinning"></i>
                </div>
                <h5>Validation in Progress</h5>
                <p>
                  AI-based validation is currently analyzing this template for CANSPAM compliance.
                  This usually takes a few seconds. Refresh to see the latest status.
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="validation-modal-footer">
            {isInvalid && this.state.isSuperUser && (
              <Button color="warning" onClick={() => { this.closeValidationModal(); this.fixWithScout(template); }}>
                <span className="scout-icon-small"></span> Fix with Scout
              </Button>
            )}
            <Button color="secondary" onClick={this.closeValidationModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
  private tokenizeTemplateName = (template_name:string, template_version: string) => {
    return template_version === Constants.TEMPLATE_VERSION.V2 ? `stripo:${template_name}` :
      template_version === Constants.TEMPLATE_VERSION.V1 ? `custom:${template_name}` : template_name
  }
  private unTokenizeTemplateName = (template_name:string) => {
    return template_name.replace('stripo:', '').replace('custom:', '')
  }
  private isTemplateSelected = (template: string) => {
    return this.state.selectedTemplateForCreation === template
  }
  private setTemplate = (template: string) => {
    this.setState({
      selectedTemplateForCreation: template,
    })

  }
  private getTemplateSelectionModal = () => {
    return (
      <div>
        <Modal isOpen={this.state.showTemplateSelectionModal}>
          <ModalHeader>
            Select a template to start with.
          </ModalHeader>
          <ModalBody className="delete-modal">
            <div className="templates">
              <div className="template-items">
                <div className={`template-item ${this.isTemplateSelected(StripoDefaultTemplates.newspaper) ? 'selected' : ''}`}
                  onClick={() => this.setTemplate(StripoDefaultTemplates.newspaper)}>
                  <div className="title">
                    Newspaper
                  </div>
                  <div className="icon">
                    <NewspaperTemplate colors={this.state.emailLayoutConfig} />
                  </div>
                </div>
                <div className={`template-item ${this.isTemplateSelected(StripoDefaultTemplates.default) ? 'selected' : ''}`}
                  onClick={() => this.setTemplate(StripoDefaultTemplates.default)}>
                  <div className="title">
                    Classic
                  </div>
                  <div className="icon">
                    <ClassicTemplate colors={this.state.emailLayoutConfig} />
                  </div>
                </div>
                <div className={`template-item ${this.isTemplateSelected(StripoDefaultTemplates.bigAndBold) ? 'selected' : ''}`}
                  onClick={() => this.setTemplate(StripoDefaultTemplates.bigAndBold)}>
                  <div className="title">
                    Big and Bold
                  </div>
                  <div className="icon">
                    <BigAndBoldTemplate colors={this.state.emailLayoutConfig} />
                  </div>
                </div>
                <div className={`template-item ${this.isTemplateSelected(StripoDefaultTemplates.insights) ? 'selected' : ''}`}
                  onClick={() => this.setTemplate(StripoDefaultTemplates.insights)}>
                  <div className="title">
                    Insights
                  </div>
                  <div className="icon">
                    <InsightsTemplate colors={this.state.emailLayoutConfig} />
                  </div>
                </div>
                <div className={`template-item ${this.isTemplateSelected(StripoDefaultTemplates.blank) ? 'selected' : ''}`}
                  onClick={() => this.setTemplate(StripoDefaultTemplates.blank)}>
                  <div className="title">
                    Blank
                  </div>
                  <div className="icon">
                    <BlankTemplate colors={this.state.emailLayoutConfig} />
                  </div>
                </div>
                {this.state.isSuperUser && (
                  <div className={`template-item ${this.isTemplateSelected(ASK_SCOUT_TEMPLATE) ? 'selected' : ''}`}
                    onClick={() => this.setTemplate(ASK_SCOUT_TEMPLATE)}>
                    <div className="title">
                      Ask Scout
                    </div>
                    <div className="icon">
                      <ScoutIcon svgwidth="60" svgheight="85" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => this.handleTemplateCreation()}>
              Create
            </Button>
            <Button onClick={this.closeTemlateSelectionModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }

  private handleTemplateCreation = () => {
    if (this.state.selectedTemplateForCreation === ASK_SCOUT_TEMPLATE) {
      // Open edit modal in Scout creation mode
      this.createTemplateWithScout()
    } else {
      // Regular template creation flow
      this.props.push(`${DashboardMenuOption.customDesign}?template=${this.unTokenizeTemplateName(this.state.selectedTemplateForCreation)}`)
    }
  }

  private createTemplateWithScout = () => {
    this.setState({
      showTemplateSelectionModal: false,
      selectedTemplateForCreation: '',
      editTemplateModal: true,
      editingTemplate: {
        id: 'new-template',
        body: '',
        fileName: 'New Template',
        type: 'custom'
      },
      templateHtml: '', // Empty - Claude will create from scratch
      templatePreview: '',
      activeTab: '2', // Start on Ask Scout tab
      saveAsMode: true,
      saveAsName: 'scout-template'
    })
    this.fetchTemplateDocumentation()
  }

  private toggleTab = (tab: string) => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab })
    }
  }

  private handleChatTemplateUpdate = (updatedHtml: string, modifications?: any) => {
    this.setState({
      templateHtml: updatedHtml,
      templatePreview: updatedHtml
    })

    // Only show success flash if HTML actually changed
    if (modifications && modifications.htmlChanged) {
      this.context.store.dispatch(Flash.showFlashMessage('Scout has updated your template!'))
    } else if (modifications && modifications.failed > 0) {
      // Show warning if modifications failed
      this.context.store.dispatch(Flash.showFlashWarning(
        `Template modifications failed. ${modifications.failed} selector${modifications.failed !== 1 ? 's' : ''} didn't match.`
      ))
    }
    // If htmlChanged is false and no failures, don't show any flash message
  }

  private handleChatError = (error: string) => {
    this.context.store.dispatch(Flash.showFlashError(error))
  }

  private renderEditTemplateModal = () => {
    return (
      <Modal
        isOpen={this.state.editTemplateModal}
        toggle={this.closeEditTemplateModal}
        className="edit-template-modal"
        modalClassName="edit-template-modal"
        size="xl"
      >
        <ModalHeader toggle={this.closeEditTemplateModal}>
          {this.state.templateHtml && this.state.templateHtml.trim()
            ? `Edit Template: ${this.state.editingTemplate && this.state.editingTemplate.fileName}`
            : 'Create Template with Scout'
          }
        </ModalHeader>
        <ModalBody className="edit-template-body">
          {this.state.isLoadingTemplate ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <Loading size="64" />
            </div>
          ) : (
            <div className="edit-template-tabbed">
              <Nav tabs>
                {/* Show HTML Editor tab only if we have HTML content */}
                {this.state.templateHtml && this.state.templateHtml.trim() && (
                  <NavItem>
                    <NavLink
                      className={this.state.activeTab === '1' ? 'active' : ''}
                      onClick={() => this.toggleTab('1')}
                    >
                      <i className="fas fa-code mr-2"></i>
                      HTML Editor
                    </NavLink>
                  </NavItem>
                )}
                {this.state.isSuperUser && (
                  <NavItem>
                    <NavLink
                      className={this.state.activeTab === '2' ? 'active' : ''}
                      onClick={() => this.toggleTab('2')}
                    >
                      <ScoutIcon svgwidth="14" svgheight="16" />
                      <span className="ml-2">Ask Scout</span>
                    </NavLink>
                  </NavItem>
                )}
              </Nav>

              <TabContent activeTab={this.state.activeTab}>
                <TabPane tabId="1">
                  <div className="edit-template-content">
                    <div className="editor-panel">
                      <textarea
                        className="html-editor"
                        value={this.state.templateHtml}
                        onChange={(e) => this.updateTemplateHtml(e.target.value)}
                        rows={20}
                        style={{
                          width: '100%',
                          fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                          fontSize: '12px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '10px'
                        }}
                      />
                    </div>
                    <div className="preview-panel">
                      <h5>Preview</h5>
                      <div
                        className="template-preview"
                        style={{
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          height: '400px',
                          overflow: 'auto',
                          padding: '10px',
                          backgroundColor: '#fff'
                        }}
                        dangerouslySetInnerHTML={{ __html: this.state.templatePreview }}
                      />
                    </div>
                  </div>
                </TabPane>

                {this.state.isSuperUser && (
                  <TabPane tabId="2">
                    <div className="edit-template-content">
                      <div className="chat-panel">
                        <TemplateChat
                          ref={this.templateChatRef}
                          communityId={this.state.communityId}
                          templateName={this.state.editingTemplate && this.state.editingTemplate.fileName || ''}
                          templateHtml={this.state.templateHtml}
                          documentation={this.state.documentation}
                          onTemplateUpdate={this.handleChatTemplateUpdate}
                          onError={this.handleChatError}
                          initialPrompt={this.state.scoutPrompt}
                        />
                      </div>
                      <div className="preview-panel">
                        <h5>Preview</h5>
                        <div
                          className="template-preview"
                          style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            height: '400px',
                            overflow: 'auto',
                            padding: '10px',
                            backgroundColor: '#fff'
                          }}
                          dangerouslySetInnerHTML={{ __html: this.state.templatePreview }}
                        />
                      </div>
                    </div>
                  </TabPane>
                )}
              </TabContent>

              <div className="save-as-section mt-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="saveAsCheckbox"
                    checked={this.state.saveAsMode}
                    onChange={this.toggleSaveAsMode}
                    disabled={this.state.isSavingTemplate}
                  />
                  <label className="form-check-label" htmlFor="saveAsCheckbox">
                    Save as new template (instead of updating existing)
                  </label>
                </div>

                {this.state.saveAsMode && (
                  <div className="save-as-name-input mt-2">
                    <label htmlFor="save-as-name" className="form-label">
                      New template name:
                    </label>
                    <input
                      type="text"
                      id="save-as-name"
                      className="form-control"
                      value={this.state.saveAsName}
                      onChange={this.handleSaveAsNameChange}
                      placeholder="Enter template name"
                      disabled={this.state.isSavingTemplate}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {this.state.isSavingTemplate ? (
            <Loading size="32" />
          ) : (
            <>
              <Button
                color="primary"
                onClick={this.state.saveAsMode ? this.saveAsNewTemplate : this.saveTemplate}
                disabled={this.state.isLoadingTemplate || this.state.isSavingTemplate || !this.state.templateHtml.trim() || (this.state.saveAsMode && !this.state.saveAsName.trim())}
              >
                {this.state.isSavingTemplate
                  ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving &amp; Validating...</>
                  : this.state.saveAsMode ? 'Save as New' : 'Save'
                }
              </Button>
              <Button color="secondary" onClick={this.closeEditTemplateModal}>
                Cancel
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    )
  }

}

export const TemplateLibrary = GenericRedux.registerNewComponent<any>(
  TemplateLibraryComponent,
  'new_design_layout',
  {})
